import { PROVIDER_API, USE_MOCK_AUTH } from "../config/api";
import { apiRequest, unwrapApiEnvelope } from "./apiClient";
import { normalizeFileUpload } from "./portfolioUploadsApi";

/** UI colour options — values are sent as `color_tag`. */
export const PORTFOLIO_COLOR_OPTIONS = [
  { value: "red", label: "Red" },
  { value: "gold", label: "Gold" },
  { value: "green", label: "Green" },
];

const COLOR_ALIASES = {
  red: "red",
  primary: "red",
  ink: "red",
  gold: "gold",
  accent: "gold",
  amber: "gold",
  yellow: "gold",
  green: "green",
  emerald: "green",
};

export function toUiColor(tag) {
  return COLOR_ALIASES[String(tag || "").toLowerCase()] || "red";
}

export function toApiColorTag(color) {
  return toUiColor(color);
}

export function normalizePortfolioGroup(raw) {
  if (!raw || typeof raw !== "object") return null;

  const groupSlug =
    raw.group_slug != null
      ? String(raw.group_slug)
      : raw.id != null
        ? String(raw.id)
        : null;
  const id = String(raw.id ?? groupSlug ?? "");
  const uploads = Array.isArray(raw.uploads)
    ? raw.uploads.map(normalizeFileUpload).filter(Boolean)
    : [];

  return {
    id,
    numericId: raw.id ?? null,
    group_slug: groupSlug,
    provider_id: raw.provider_id ?? null,
    name: raw.group_name || raw.name || "",
    group_name: raw.group_name || raw.name || "",
    description: raw.description || "",
    color: "red",
    color_tag: "red",
    createdAt: raw.created_at || raw.createdAt || null,
    updatedAt: raw.updated_at || raw.updatedAt || null,
    uploadedAt: raw.uploaded_at ?? raw.uploadedAt ?? null,
    failedAt: raw.failed_at ?? raw.failedAt ?? null,
    numberOfCases: Number(raw.cases_count ?? raw.number_of_cases ?? raw.numberOfCases ?? 0),
    portfoliosCount: Number(raw.portfolios_count ?? raw.portfoliosCount ?? 0),
    uploadsCount: Number(raw.uploads_count ?? raw.uploadsCount ?? uploads.length),
    totalAmountOwed: Number(raw.total_amount_owed ?? raw.totalAmountOwed ?? 0),
    totalOutstanding: Number(raw.total_outstanding ?? raw.totalOutstanding ?? 0),
    deletedAt: raw.deleted_at ?? raw.deletedAt ?? null,
    uploads,
  };
}

/** Merge upload list data with case/portfolio totals from the API. */
export function enrichPortfolioGroup(group) {
  if (!group) return group;

  const uploads = group.uploads ?? [];
  const accountsFromUploads = uploads.reduce((sum, row) => sum + (Number(row.accounts) || 0), 0);
  const owedFromUploads = uploads.reduce((sum, row) => sum + (Number(row.totalOwed) || 0), 0);
  const cases = Number(group.numberOfCases) || 0;
  const portfolios = Number(group.portfoliosCount) || 0;
  const bookOwed = Number(group.totalAmountOwed) || 0;
  const outstanding = Number(group.totalOutstanding) || 0;
  const uploadCount = uploads.length || Number(group.uploadsCount) || 0;
  const latestUpload = uploads[0] ?? null;

  const accounts = accountsFromUploads || cases || portfolios;
  const owed = owedFromUploads || bookOwed || outstanding;
  const hasPortfolioData = uploadCount > 0 || cases > 0 || portfolios > 0 || owed > 0;

  return {
    ...group,
    accounts,
    owed,
    uploadCount,
    latestUpload,
    hasPortfolioData,
  };
}

function extractGroups(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.portfolio_groups)) return data.portfolio_groups;
  if (Array.isArray(data?.groups)) return data.groups;
  if (Array.isArray(data?.data)) return data.data;
  return [];
}

function groupPath(slug) {
  return `${PROVIDER_API.portfolioGroups}/${encodeURIComponent(slug)}`;
}

function buildGroupBody({ name, description, color, group_name, color_tag }) {
  const groupName = String(group_name || name || "").trim();
  if (!groupName) throw new Error("Group name is required.");
  return {
    group_name: groupName,
    description: String(description || "").trim(),
  };
}

/**
 * GET /api/provs/portfolio-groups
 */
export async function fetchPortfolioGroups(token, params = {}) {
  if (USE_MOCK_AUTH || !token) return null;

  const query = new URLSearchParams();
  // Backend validates `limit` (not per_page).
  query.set("limit", String(params.limit || params.per_page || 50));
  query.set("sort_order", params.sort_order || "desc");
  if (params.page) query.set("page", String(params.page));
  if (params.search) query.set("search", params.search);

  const json = await apiRequest(`${PROVIDER_API.portfolioGroups}?${query.toString()}`, {
    method: "GET",
    token,
  });
  const data = unwrapApiEnvelope(json);
  const groups = extractGroups(data).map(normalizePortfolioGroup).filter(Boolean);
  return {
    groups,
    pagination: data?.pagination || null,
  };
}

let groupsInFlight = { token: null, promise: null };

export async function fetchAllPortfolioGroups(token, { force = false } = {}) {
  if (USE_MOCK_AUTH || !token) return [];

  if (!force && groupsInFlight.token === token && groupsInFlight.promise) {
    return groupsInFlight.promise;
  }

  const promise = (async () => {
    const first = await fetchPortfolioGroups(token, { page: 1, limit: 50 });
    if (!first) return [];

    const lastPage = Number(first.pagination?.last_page || 1);
    if (lastPage <= 1) return first.groups;

    const rest = await Promise.all(
      Array.from({ length: lastPage - 1 }, (_, i) =>
        fetchPortfolioGroups(token, { page: i + 2, limit: 50 })
      )
    );
    return first.groups.concat(rest.flatMap((page) => page?.groups || []));
  })();

  groupsInFlight = { token, promise };
  return promise;
}

export function clearPortfolioGroupsCache() {
  groupsInFlight = { token: null, promise: null };
}

/**
 * GET /api/provs/portfolio-groups/{group_slug}
 */
export async function fetchPortfolioGroup(token, groupSlug) {
  if (USE_MOCK_AUTH || !token) return null;
  const json = await apiRequest(groupPath(groupSlug), { method: "GET", token });
  const data = unwrapApiEnvelope(json);
  return normalizePortfolioGroup(data?.portfolio_group || data);
}

/**
 * POST /api/provs/portfolio-groups
 */
export async function createPortfolioGroup(token, payload) {
  const body = buildGroupBody(payload);

  if (USE_MOCK_AUTH || !token) {
    return normalizePortfolioGroup({
      id: Date.now(),
      group_slug: `grp_${Date.now()}`,
      ...body,
      number_of_cases: 0,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    });
  }

  const json = await apiRequest(PROVIDER_API.portfolioGroups, {
    method: "POST",
    token,
    body,
  });
  const data = unwrapApiEnvelope(json);
  return normalizePortfolioGroup(data?.portfolio_group || data);
}

/**
 * PUT /api/provs/portfolio-groups/{group_slug}
 */
export async function updatePortfolioGroup(token, groupSlug, payload) {
  const body = buildGroupBody(payload);

  if (USE_MOCK_AUTH || !token) {
    return normalizePortfolioGroup({
      group_slug: groupSlug,
      ...body,
      updated_at: new Date().toISOString(),
    });
  }

  const json = await apiRequest(groupPath(groupSlug), {
    method: "PUT",
    token,
    body,
  });
  const data = unwrapApiEnvelope(json);
  return normalizePortfolioGroup(data?.portfolio_group || data);
}

/**
 * DELETE /api/provs/portfolio-groups/{group_slug}
 */
export async function deletePortfolioGroup(token, groupSlug) {
  if (USE_MOCK_AUTH || !token) return true;

  await apiRequest(groupPath(groupSlug), { method: "DELETE", token });
  return true;
}

/**
 * GET /api/provs/portfolio-groups/trashed
 */
export async function fetchTrashedPortfolioGroups(token) {
  if (USE_MOCK_AUTH || !token) return [];

  const json = await apiRequest(`${PROVIDER_API.portfolioGroups}/trashed`, {
    method: "GET",
    token,
  });
  const data = unwrapApiEnvelope(json);
  return extractGroups(data).map(normalizePortfolioGroup).filter(Boolean);
}

/**
 * POST /api/provs/portfolio-groups/{id}/restore
 */
export async function restorePortfolioGroup(token, groupSlug) {
  if (USE_MOCK_AUTH || !token) return true;

  await apiRequest(`${groupPath(groupSlug)}/restore`, { method: "POST", token });
  return true;
}

/**
 * DELETE /api/provs/portfolio-groups/{id}/permanent
 */
export async function permanentlyDeletePortfolioGroup(token, groupSlug) {
  if (USE_MOCK_AUTH || !token) return true;

  await apiRequest(`${groupPath(groupSlug)}/permanent`, { method: "DELETE", token });
  return true;
}
